import { useCallback, useRef } from "react";

import type { AuditEvent } from "../../../models/audit/auditEvent";
import { Skeleton } from "../../ui/skeleton";
import { AuditEventRow } from "./AuditEventRow";

interface AuditEventListProps {
  events: AuditEvent[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  memberEmailById?: (userId: string) => string | undefined;
}

function SkeletonRows() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex gap-3">
          <Skeleton className="h-6 w-6 flex-shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5 pt-0.5">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A feed of audit events: rows joined by a vertical timeline rule, a loading
 * skeleton, an empty state, and infinite scroll via an IntersectionObserver
 * on the last row (the house pattern from `BackgroundTasksPopover`). Data
 * comes entirely from props so any of `useSessionAuditEvents` /
 * `useWorkspaceAuditEvents` can feed it without this component knowing which.
 */
export function AuditEventList({
  events,
  loading,
  hasMore,
  loadMore,
  memberEmailById,
}: AuditEventListProps) {
  const observer = useRef<IntersectionObserver | null>(null);
  const lastRowRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMore],
  );

  if (loading && events.length === 0) {
    return <SkeletonRows />;
  }

  if (!loading && events.length === 0) {
    return (
      <div className="py-2 text-xs text-stone-500">No audit events yet</div>
    );
  }

  return (
    <div className="space-y-3">
      {events.map((event, index) => (
        <div
          key={event.id}
          ref={index === events.length - 1 ? lastRowRef : undefined}
        >
          <AuditEventRow
            event={event}
            isLast={index === events.length - 1}
            memberEmailById={memberEmailById}
          />
        </div>
      ))}
      {loading && (
        <div className="flex justify-center py-2">
          <Skeleton className="h-3 w-24" />
        </div>
      )}
    </div>
  );
}
